import React, { useRef, useState } from "react";
import {
  Avatar,
  Badge,
  Grid,
  IconButton,
  makeStyles,
  Typography,
  Button
} from "@material-ui/core";
import { connect } from "react-redux";
import LockIcon from "@material-ui/icons/Lock";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import { setShowingRouteId } from "../../store/actions/project";
import {
  checkIsFOJW,
  checkShouldRouteEtaBecomeRed,
  checkShouldRouteUnitsBecomeBolder,
  distanceStr,
  getRouteUnitsLabel,
  hoursMinsFromSeconds,
  secondsToTime
} from "../../utils/helpers";
import AccountCircleIcon from "@material-ui/icons/AccountCircle";
import OptimizedTooltip from "../common/OptimizedTooltip";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import RouteHeaderRoutesMenu from "./RouteHeaderRoutesMenu";
import RouteToolbar from "./RouteToolbar";
import AssignDriverMenu from "./AssignDriverMenu";
import useRouteData from "../../hooks/useRouteInfo";
import clsx from "clsx";
import {
  LocalShippingOutlined,
  PersonAddOutlined,
  PersonOutlineOutlined
} from "@material-ui/icons";
import { LANDING_URL } from "../data/constants";
import AssignVehicleMenu from "./AssignVehicleMenu";
import useIsTruncated from "./useIsTruncated";

const useStyles = makeStyles(theme => ({
  wrapper: {
    padding: theme.spacing(0.525, 2),
    borderStyle: "solid",
    borderColor: "#EAEBF0",
    borderWidth: 0,
    borderBottomWidth: 1
  },
  descriptionWrapper: {
    margin: theme.spacing(0, 2)
  },
  routeLabel: {
    maxWidth: "100%",
    marginBottom: 2,
    fontSize: theme.typography.pxToRem(14),
    fontWeight: theme.typography.fontWeightMedium
  },
  driverAvatarButton: {
    padding: 7
  },
  driverAvatar: {
    width: 26,
    height: 26,
    backgroundColor: "#1329FE",
    fontSize: theme.typography.pxToRem(12),
    fontWeight: theme.typography.fontWeightMedium
  },
  noDriverAvatarIcon: {
    fontSize: theme.typography.pxToRem(26),
    color: "#C2C3C7"
  },
  liveStatusBadge: {
    top: "17%",
    right: "17%"
  },
  liveStatusBadgeColorSecondary: {
    backgroundColor: "#1DA25E"
  },
  timeStamps: {
    fontSize: theme.typography.pxToRem(14),
    color: "#4D4D50",
    paddingRight: theme.spacing(1)
  },
  routeActionIcon: {
    marginRight: "8px",
    fontSize: "20px",
    color: "#1329FE"
  },
  assignedRouteActionIcon: {
    color: "#606165"
  },
  assignRouteActionButton: {
    border: "1px solid #EAEAEA",
    padding: "5px 8px",
    color: "#1329FE",
    fontSize: "14px",
    lineHeight: "16px",
    fontWeight: 500,
    marginTop: 4,
    "&:disabled svg": {
      color: "#B6B6B6"
    }
  },
  assignedRouteActionButton: {
    color: "#4D4D50",
    width: 120
  },
  routeActions: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: "4px"
  },
  boldUnit: {
    fontWeight: "900",
    // We want to emphasize this unit
    color: theme.palette.error.main
  },
  lateEta: {
    // Last-customer ETA at/after 16:00 — FOJW "needs adjusting" signal (mirrors boldUnit).
    fontWeight: 700,
    color: theme.palette.error.main
  },
  routeActionsButtonLabel: {
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    textOverflow: "ellipsis",
    lineHeight: theme.typography.pxToRem(16),
    textAlign: "left",
    fontSize: theme.typography.pxToRem(14),
    fontWeight: `500`,
    color: "inherit"
  }
}));

function RouteHeader(props) {
  const {
    backhaulGroup,
    setShowingRouteId,
    distanceUnits,
    plan,
    isPastProject,
    driversLiveStatus,
    selectedStops,
    handleSelectAllClick,
    searchInputValue,
    setSearchInputValue,
    published,
    onSearchFocus,
    onSearchBlur
  } = props;

  // General
  const classes = useStyles();

  // States
  const [menuOpen, setMenuOpen] = useState(false);
  const [assignDriverAnchorElement, setAssignDriverAnchorElement] =
    useState(null);
  const [assignVehicleAnchorElement, setAssignVehicleAnchorElement] =
    useState(null);

  // Refs
  const menuButtonRef = useRef(undefined);

  // Variable
  // const allRouteIds = backhaulGroup.map(routeGroup => routeGroup.id);
  // const allStops = backhaulGroup.map(routeGroup => routeGroup.stops).flat();
  // const allNonStartEndStops = allStops.filter(
  //   stop => stop.type !== "start" && stop.type !== "end"
  // );

  // const allStarts = allStops.filter(stop => stop.type === "start");
  // const allEnds = allStops.filter(stop => stop.type === "end");

  // const startEndStops =
  //   allNonStartEndStops.length > 0
  //     ? [allStarts[0], allEnds[allStarts.length - 1]]
  //     : null;

  const {
    allRouteIds,
    allStops,
    unitsCount,
    startEndStops,
    distances,
    completionTime,
    allNonStartEndBreakStops,
    lastCustomerEta,
    mainRoute
  } = useRouteData(backhaulGroup);

  const live = driversLiveStatus
    ? Object.keys(driversLiveStatus).some(routeId =>
        allRouteIds.includes(routeId)
      )
    : false;

  // Callbacks
  const openMenu = () => {
    setMenuOpen(true);
  };

  const closeMenu = () => {
    setMenuOpen(false);
  };

  const unitsLabel = getRouteUnitsLabel(unitsCount);
  const hasRouteDriver = mainRoute.driver !== null;
  const hasRouteVehicle = mainRoute.vehicle_name !== null;
  const isFOJW = checkIsFOJW();

  // FOJW shows the ETA at the last customer (not the depot return / "terug op de zaak").
  const endTimeValue =
    lastCustomerEta != null
      ? lastCustomerEta
      : startEndStops && startEndStops[1]?.arr_time;
  const etaIsLate = checkShouldRouteEtaBecomeRed(lastCustomerEta, isFOJW);

  // After mainRoute is available
  const vehicleLabel = mainRoute.vehicle_name || "Assign vehicle";
  const [vehicleRef, isVehicleTruncated] = useIsTruncated([vehicleLabel]);
  const driverLabel = mainRoute.driver
    ? `${mainRoute.driver.first_name || ""} ${
        mainRoute.driver.last_name || ""
      }`.trim()
    : "Assign driver";
  const [driverRef, isDriverTruncated] = useIsTruncated([driverLabel]);

  return (
    <>
      <Grid
        container
        wrap="nowrap"
        alignItems="center"
        className={classes.wrapper}
        style={
          isFOJW
            ? {
                minHeight: 88
              }
            : {}
        }
      >
        <Grid item>
          <IconButton onClick={() => setShowingRouteId("")}>
            <ArrowBackIcon />
          </IconButton>
        </Grid>
        <Grid item className={classes.descriptionWrapper}>
          {!isFOJW && (
            <>
              <div
                style={{ display: "inline-block", maxWidth: "220px" }}
                ref={menuButtonRef}
              >
                <Typography className={classes.routeLabel} noWrap>
                  {mainRoute.locked && (
                    <LockIcon style={{ fontSize: 14, verticalAlign: -2.5 }} />
                  )}
                  {mainRoute.vehicle_name || "No label"}
                </Typography>
              </div>
              <div style={{ display: "inline-block" }}>
                <KeyboardArrowDownIcon
                  onClick={openMenu}
                  style={{ cursor: "pointer" }}
                />
              </div>
            </>
          )}
          <Typography
            color="secondary"
            variant="body2"
            style={{ fontSize: 13 }}
          >
            {isFOJW && mainRoute.locked && (
              <LockIcon
                style={{ fontSize: 14, verticalAlign: -2.5, marginRight: 2 }}
              />
            )}
            {allNonStartEndBreakStops.length} stops •{" "}
            {distanceStr(distances, distanceUnits === "km")} •{" "}
            {hoursMinsFromSeconds(completionTime)}
            {<span> • </span>}
            {
              <span
                className={clsx({
                  [classes.boldUnit]: checkShouldRouteUnitsBecomeBolder(
                    unitsCount,
                    isFOJW
                  )
                })}
              >
                {unitsLabel}
              </span>
            }
          </Typography>
          {isFOJW && (
            <Grid item className={clsx(classes.routeActions)}>
              <Button
                className={clsx(classes.assignRouteActionButton, {
                  [classes.assignedRouteActionButton]: hasRouteVehicle
                })}
                disabled={published}
                onClick={event => {
                  event.stopPropagation();
                  setAssignVehicleAnchorElement(event.currentTarget);
                }}
              >
                <LocalShippingOutlined
                  className={clsx(classes.routeActionIcon, {
                    [classes.assignedRouteActionIcon]: hasRouteVehicle
                  })}
                />
                <OptimizedTooltip
                  placement="bottom"
                  title={isVehicleTruncated ? vehicleLabel : ""}
                >
                  <Typography
                    ref={vehicleRef}
                    className={clsx(classes.routeActionsButtonLabel)}
                  >
                    {vehicleLabel}
                  </Typography>
                </OptimizedTooltip>
              </Button>
              <Button
                className={clsx(classes.assignRouteActionButton, {
                  [classes.assignedRouteActionButton]: hasRouteDriver
                })}
                disabled={published}
                onClick={event => {
                  event.stopPropagation();
                  setAssignDriverAnchorElement(event.currentTarget);
                }}
              >
                {mainRoute.driver ? (
                  <PersonOutlineOutlined
                    className={clsx(classes.routeActionIcon, {
                      [classes.assignedRouteActionIcon]: hasRouteDriver
                    })}
                  />
                ) : (
                  <PersonAddOutlined
                    className={clsx(classes.routeActionIcon, {
                      [classes.assignedRouteActionIcon]: hasRouteDriver
                    })}
                  />
                )}
                <OptimizedTooltip
                  placement="bottom"
                  title={isDriverTruncated ? driverLabel : ""}
                >
                  <Typography
                    ref={driverRef}
                    className={clsx(classes.routeActionsButtonLabel)}
                  >
                    {driverLabel}
                  </Typography>
                </OptimizedTooltip>
              </Button>
            </Grid>
          )}
        </Grid>
        <Grid item xs>
          <Grid container justify="flex-end" alignItems="center">
            <Grid item>
              {startEndStops && (
                <Typography className={classes.timeStamps}>
                  {`${secondsToTime(
                    startEndStops[0]?.finish_time || startEndStops[0]?.end_time
                  )}-`}
                  <span className={clsx({ [classes.lateEta]: etaIsLate })}>
                    {secondsToTime(endTimeValue)}
                  </span>
                </Typography>
              )}
            </Grid>
            {!isFOJW && (
              <Grid item>
                <OptimizedTooltip
                  placement="bottom"
                  title={
                    mainRoute.driver
                      ? `Assigned to ${mainRoute.driver.first_name || ""} ${
                          mainRoute.driver.last_name || ""
                        }`
                      : published
                      ? "Route unassigned"
                      : "Assign driver"
                  }
                >
                  <span>
                    <IconButton
                      disabled={published}
                      onClick={event => {
                        setAssignDriverAnchorElement(event.currentTarget);
                      }}
                      className={classes.driverAvatarButton}
                    >
                      <Badge
                        invisible={
                          !(plan && plan.ft_realtime_driver_tracking) ||
                          !(live || allStops[0].status === "finished") ||
                          isPastProject ||
                          allStops[allStops.length - 1].status === "finished"
                        }
                        classes={{
                          badge: classes.liveStatusBadge,
                          colorSecondary: classes.liveStatusBadgeColorSecondary
                        }}
                        overlap="circle"
                        anchorOrigin={{
                          vertical: "top",
                          horizontal: "right"
                        }}
                        variant="dot"
                        color={
                          live || allStops[0].status === "finished"
                            ? live
                              ? "secondary"
                              : "error"
                            : ""
                        }
                      >
                        {mainRoute.driver ? (
                          <Avatar className={classes.driverAvatar}>{`${
                            mainRoute.driver.first_name
                              ? mainRoute.driver.first_name[0]
                              : ""
                          }${
                            mainRoute.driver.last_name
                              ? mainRoute.driver.last_name[0]
                              : ""
                          }`}</Avatar>
                        ) : (
                          <AccountCircleIcon
                            className={classes.noDriverAvatarIcon}
                          />
                        )}
                      </Badge>
                    </IconButton>
                  </span>
                </OptimizedTooltip>
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>
      <AssignDriverMenu
        anchorElement={assignDriverAnchorElement}
        mainRouteId={mainRoute.id}
        onClose={() => setAssignDriverAnchorElement(null)}
      />
      <AssignVehicleMenu
        anchorElement={assignVehicleAnchorElement}
        mainRouteId={mainRoute.id}
        onClose={() => setAssignVehicleAnchorElement(null)}
      />
      <RouteToolbar
        backhaulGroup={backhaulGroup}
        selectedStops={selectedStops}
        handleSelectAllClick={handleSelectAllClick}
        allStops={allNonStartEndBreakStops}
        locked={mainRoute.locked}
        searchInputValue={searchInputValue}
        setSearchInputValue={setSearchInputValue}
        onSearchFocus={onSearchFocus}
        onSearchBlur={onSearchBlur}
      />
      <RouteHeaderRoutesMenu
        menuOpen={menuOpen}
        closeMenu={closeMenu}
        menuButtonRef={menuButtonRef}
        resetStopsSearchInput={() => setSearchInputValue("")}
      />
    </>
  );
}

const mapStateToProps = state => ({
  showingRouteId: state.project.showingRouteId,
  distanceUnits: state.project.vrp.configuration.distance_units,
  driversLiveStatus: state.map.driversLiveStatus,
  plan:
    (state.auth.userData.organisationSite &&
      state.auth.userData.organisationSite.organisation.plan) ||
    {},
  isPastProject: ["expired", "finished"].includes(
    state.project.status.toLowerCase()
  ),
  published: ["published", "expired", "finished"].includes(
    state.project.status.toLowerCase()
  )
});

export default connect(mapStateToProps, { setShowingRouteId })(RouteHeader);
